import {
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  message,
  Segmented,
  Select,
  Skeleton,
  Tooltip,
} from 'antd';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import File from '../../interfaces/File';
import FileAccess from '../../interfaces/FileAccess';
import { GetFileAccessesPayload } from '../../interfaces/FileService';
import Group from '../../interfaces/Group';
import User from '../../interfaces/User';
import { FileService } from '../../services/fileService';
import {
  copyToClipboard,
  localDateFromUtcTicks,
  msToTicks,
  ticksToMs,
} from '../../utils/utils';
import UserMultiSelect from '../Common/UserMultiSelect';
import { Icon } from '../Core/Icon';
dayjs.extend(utc);

interface Props {
  selectedFiles: File[];
  authenticatedUser: User | null;
  onBehalfOfUser?: User | null;
  onSubmit: (
    newUsers: User[],
    newGroups: Group[],
    fileAccessesToRemove: FileAccess[],
    fileAccessesToUpdate: FileAccess[],
    fileAccessesRevokeDenied: FileAccess[],
    sendMessage: boolean,
    message: string,
    readOnly: boolean,
    expiry: number
  ) => void;
  onCancel: () => void;
}

interface FormState {
  users: User[];
  groups: Group[];
  fileAccessesToRemove: FileAccess[];
  fileAccessesToUpdate: FileAccess[];
  fileAccessesRevokeDenied: FileAccess[];
  sendMessage: boolean;
  message: string;
  readOnly: boolean;
  expiry: number;
}

const fileService = new FileService();

export const FileShareForm = ({
  selectedFiles,
  onSubmit,
  onCancel,
  authenticatedUser,
  onBehalfOfUser,
}: Props) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  const [fileAccesses, setFileAccesses] = useState<FileAccess[]>([]);
  const [form, setForm] = useState<FormState>({
    users: [],
    groups: [],
    fileAccessesToRemove: [],
    fileAccessesToUpdate: [],
    fileAccessesRevokeDenied: [],
    sendMessage: true,
    message: '',
    readOnly: false,
    expiry: 0,
  });

  const [isLimitationsDatePickerOpen, setIsLimitationsDatePickerOpen] =
    useState(false);
  const [limitationsDatePickerValue, setLimitationsDatePickerValue] =
    useState<Dayjs | null>(null);

  const [isFileAccessDatePickerOpen, setIsFileAccessDatePickerOpen] =
    useState(false);
  const [fileAccessDatePickerValue, setFileAccessDatePickerValue] =
    useState<Dayjs | null>(null);
  const [selectedFileAccessForDatePicker, setSelectedFileAccessForDatePicker] =
    useState<FileAccess | null>(null);

  const [hasMore, setHasMore] = useState(true);
  const [totalFileAccesses, setTotalFileAccesses] = useState(0);
  const [filters, setFilters] = useState<GetFileAccessesPayload>({
    offset: 0,
    count: 10,
    search: '',
    files: selectedFiles.map((file) => file.fileId),
  });
  const [isLoading, setIsLoading] = useState(true);
  const scrollableDivRef = useRef<HTMLDivElement>(null);

  /** Fetches file access information for the selected files.
   *
   * This function retrieves file access information for the files specified in the `selectedFiles` array.
   * It uses the `getFileAccesses` method from the `fileService` to fetch the data.
   *
   * Dependencies:
   * - `filters`: The current filters used for fetching file access information.
   * - `onBehalfOfUser`: The user on behalf of which the request is made.
   */
  const getFileAccesses = useCallback(() => {
    const payload: GetFileAccessesPayload = {
      ...filters,
      files: selectedFiles.map((file) => file.fileId),
    };
    if (onBehalfOfUser) {
      payload.onBehalfOf = onBehalfOfUser.userId;
    }

    fileService
      .getFileAccesses(payload)
      .then((response) => {
        const users = response?.accesses || [];

        if (authenticatedUser) {
          users.unshift({
            id: -1,
            user: {
              userId: authenticatedUser.userId,
              email: authenticatedUser.email,
              firstName: authenticatedUser.firstName || '',
              lastName: authenticatedUser.lastName || '',
              role: authenticatedUser.role || UserRoleEnum.Contributor,
            },
            fileLimitation: {
              canEdit: false,
              accessExpiration: 0,
              canDownload: false,
              canOpenWithOffice: false,
              antiCapture: false,
              accessDenied: false,
            },
            isInherited: false,
          });
        }

        if (filters.offset === 0) {
          setFileAccesses(users);
        } else {
          setFileAccesses((prev) => [...prev, ...users]);
        }

        setTotalFileAccesses(response?.total || 0);

        if (response?.accesses?.length < filters.count) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters, onBehalfOfUser]);

  /**
   * Scroll event handler: if user is near the bottom of the list and hasMore is true,
   * increment the offset to load more items. We also check isLoading to avoid double-fetches.
   */
  const handleScroll = () => {
    if (!scrollableDivRef.current) return;
    if (isLoading || !hasMore) return;

    const { scrollTop, clientHeight, scrollHeight } = scrollableDivRef.current;
    // e.g., 50px from the bottom:
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      // Load next chunk
      setFilters((prev) => ({
        ...prev,
        offset: prev.offset + prev.count,
      }));
      setIsLoading(true);
    }
  };

  const handleSubmit = () => {
    onSubmit(
      form.users,
      form.groups,
      form.fileAccessesToRemove,
      form.fileAccessesToUpdate,
      form.fileAccessesRevokeDenied,
      form.sendMessage,
      form.message,
      form.readOnly,
      form.expiry
    );
  };

  /**
   * Handles changes to form fields.
   *
   * This function updates the form state with the new value for a specified field.
   * It takes a field name and a value, and updates the form state accordingly.
   *
   * Dependencies:
   * - `form`: The current form state.
   * - `field`: The field to update.
   * - `value`: The new value for the field.
   */
  const handleFormFieldChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handles changes to the file limitation.
   *
   * This function updates the file limitation for a given file access.
   * It finds the original limitation, checks if there is already an override,
   * builds the new limitation by merging the original with the override and the new field,
   * and removes the original if it is the same as the new limitation.
   *
   * @param fileAccess The file access to update.
   * @param field The field to update.
   * @param value The new value for the field.
   */
  const handleFileLimitationChange = (
    fileAccess: FileAccess,
    field: keyof FileAccess['fileLimitation'],
    value: any
  ) => {
    setForm((prev) => {
      // Find the original limitation
      const original = fileAccess.fileLimitation;

      //  Find if there is already an override in fileAccessesToUpdate
      const existingOverride = prev.fileAccessesToUpdate.find(
        (fa) => fa.id === fileAccess.id
      );

      // Build the new limitation by merging original + override + new field
      const base = existingOverride
        ? existingOverride.fileLimitation
        : original;
      const newLimitation: FileAccess['fileLimitation'] = {
        ...base,
        [field]: value,
      };

      // Remove the original if it is the same as the new limitation
      let newUpdates = prev.fileAccessesToUpdate.filter(
        (fa) => fa.id !== fileAccess.id
      );
      // Check  if the new limitation is different from the original
      if (JSON.stringify(newLimitation) !== JSON.stringify(original)) {
        // Otherwise insert it back
        const entry: FileAccess = {
          ...fileAccess,
          fileLimitation: newLimitation,
        };
        newUpdates = [...newUpdates, entry];
      }

      return {
        ...prev,
        fileAccessesToUpdate: newUpdates,
      };
    });
  };

  /**
   * Checks if a file access is marked for removal.
   *
   * This function determines whether a file access exists in the list of file accesses to be removed.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToRemove` array, which contains the file accesses marked for removal.
   *
   */
  const isFileAccessMarkedToRemove = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesToRemove?.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesToRemove]
  );

  /**
   * Checks if a file access is marked for Revoke Deny.
   *
   * This function determines whether a file access exists in the list of file accesses to be removed.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToRemove` array, which contains the file accesses marked for removal.
   *
   */
  const isFileAccessMarkedToRevokeDeny = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesRevokeDenied?.find(
        (f) => f.id === fileAccess.id
      );
    },
    [form.fileAccessesRevokeDenied]
  );

  /**
   * Checks if a file access is marked for editing.
   *
   * This function determines whether a file access exists in the list of file accesses to be updated.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToUpdate` array, which contains the file accesses marked for editing.
   *
   */
  const isFileAccessMarkedToUpdate = useCallback(
    (fileAccess: FileAccess) => {
      return form.fileAccessesToUpdate.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesToUpdate]
  );

  /**
   * Checks if new users or groups are selected.
   *
   * This function determines whether new users or groups are selected in the file share form.
   *
   * Dependencies:
   * - `users`: An array of selected users.
   * - `groups`: An array of selected groups.
   *
   * Returns:
   * - `true` if either `users` or `groups` is not empty.
   * - `false` otherwise.
   */
  const areNewUsersOrGroupsSelected = useMemo(() => {
    return form.users.length > 0 || form.groups.length > 0;
  }, [form.users, form.groups]);

  /**
   * Checks if the submit button is disabled.
   *
   * This function determines whether the submit button should be disabled.
   *
   * Dependencies:
   * - `isLoading`: Whether the form is loading.
   * - `areNewUsersOrGroupsSelected`: Whether new users or groups are selected.
   */
  const isSubmitButtonDisabled = useMemo(() => {
    if (isLoading) return true;

    if (areNewUsersOrGroupsSelected) {
      return form.users.length === 0 && form.groups.length === 0;
    }

    const hasAnyAccessChange =
      form.fileAccessesToRemove.length > 0 ||
      form.fileAccessesToUpdate.length > 0 ||
      form.fileAccessesRevokeDenied.length > 0;

    return !hasAnyAccessChange;
  }, [
    isLoading,
    areNewUsersOrGroupsSelected,
    form.users.length,
    form.groups.length,
    form.fileAccessesToRemove.length,
    form.fileAccessesToUpdate.length,
    form.fileAccessesRevokeDenied.length,
  ]);

  useEffect(() => {
    getFileAccesses();
  }, [getFileAccesses]);

  return (
    <Form
      layout="vertical"
      size="large"
      className="pt-6 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden [&_.ant-form-item]:mb-2"
      //onFinish={handleFinish}
    >
      {contextHolder}
      <div className="flex flex-col gap-2">
        <div
          className={clsx(
            'flex w-full flex-row items-center divide-x divide-gray-medium rounded-md',
            areNewUsersOrGroupsSelected
              ? 'border border-gray-medium'
              : 'border-transparent'
          )}
        >
          <UserMultiSelect
            className="w-full p-0"
            variant={areNewUsersOrGroupsSelected ? 'borderless' : 'outlined'}
            ignoredUserIds={[authenticatedUser?.userId || 0]}
            forbiddenTagNames={[authenticatedUser?.email || '']}
            closeOnSelect
            size="large"
            autoClearSearchValue
            maxTagCount={10}
            allowClear={false}
            disabled={isLoading}
            selectedUserIds={form.users.map((user) => user?.userId)}
            selectedGroupIds={form.groups.map((group) => group?.id)}
            onUserChange={(_, values) => {
              handleFormFieldChange('users', values);
            }}
            onGroupChange={(_, values) =>
              handleFormFieldChange('groups', values)
            }
            additionalUserFilters={{
              roleFilter: [
                UserRoleEnum.Admin,
                UserRoleEnum.Contributor,
                UserRoleEnum.Reader,
              ],
            }}
            mode="tags"
          />
          {areNewUsersOrGroupsSelected && (
            <Select
              className="p-0"
              variant="borderless"
              placement="bottomRight"
              value={form.readOnly}
              onChange={(value) => handleFormFieldChange('readOnly', value)}
              optionRender={(option) => {
                return (
                  <div className="flex w-full min-w-32 flex-row items-center gap-2">
                    {option.data.icon}
                    <span>{option.data.label}</span>
                  </div>
                );
              }}
              popupMatchSelectWidth={false}
              labelRender={() => {
                return (
                  <Tooltip
                    title={
                      form.readOnly ? t('common.viewer') : t('common.editor')
                    }
                  >
                    <div>
                      <Icon
                        name={form.readOnly ? 'ViewIcon' : 'SquarePenIcon'}
                        className="h-6 w-6"
                      />
                    </div>
                  </Tooltip>
                );
              }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />

                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setIsLimitationsDatePickerOpen(true);
                      if (form.expiry) {
                        // value in UTC
                        setLimitationsDatePickerValue(
                          localDateFromUtcTicks(form.expiry)
                        );
                      } else {
                        setLimitationsDatePickerValue(null);
                      }
                    }}
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Icon name="CalendarFoldIcon" className="h-5 w-5" />
                      <span>
                        {form.expiry
                          ? t('common.editExpirationDate')
                          : t('common.addExpirationDate')}
                      </span>
                    </div>
                  </Button>
                </>
              )}
              options={[
                {
                  value: true,
                  icon: <Icon name="ViewIcon" className="h-5 w-5" />,
                  label: t('common.viewer'),
                },
                {
                  value: false,
                  icon: <Icon name="SquarePenIcon" className="h-5 w-5" />,
                  label: t('common.editor'),
                },
              ]}
            />
          )}
        </div>

        {areNewUsersOrGroupsSelected && form.expiry !== 0 && (
          <div className="flex flex-row items-center justify-start gap-2">
            <span className="text-gray-500 text-sm">
              {t('common.expirationDate')}:
            </span>
            <Tooltip title={t('common.removeExpirationDate')}>
              <Button
                className="py-0 text-xs"
                onClick={() => {
                  handleFormFieldChange('expiry', 0);
                }}
                size="small"
                disabled={!form.expiry}
              >
                <div className="flex flex-row items-center gap-2">
                  <span>
                    {dayjs
                      .utc(ticksToMs(form.expiry))
                      .format('YYYY-MM-DD HH:mm [UTC]')}
                  </span>
                  <Icon name="XIcon" className="h-5 w-5" />
                </div>
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {areNewUsersOrGroupsSelected ? (
        <div className="mt-4 flex flex-col gap-2">
          <Checkbox
            checked={form.sendMessage}
            onChange={(e) =>
              handleFormFieldChange('sendMessage', e.target.checked)
            }
          >
            {t('modal.fileShareForm.sendMessage')}
          </Checkbox>
          {form.sendMessage && (
            <Input.TextArea
              value={form.message}
              onChange={(e) => handleFormFieldChange('message', e.target.value)}
              rows={4}
              placeholder={t('modal.fileShareForm.sendMessage')}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Divider />
          <span className="text-lg font-medium">
            {t('modal.fileShareForm.peopleWithAccess')}
          </span>
          <OverlayScrollbarsComponent
            options={{
              scrollbars: {
                autoHide: 'leave',
              },
            }}
            defer
            style={{ height: '100%', overflow: 'auto' }}
          >
            <div
              ref={scrollableDivRef}
              onScroll={handleScroll}
              className="mb-4 flex max-h-80 flex-col overflow-y-auto pr-2"
            >
              <List<FileAccess>
                dataSource={fileAccesses}
                loading={isLoading}
                locale={{ emptyText: t('common.noUsers') }}
                renderItem={(fileAccess) => {
                  const isFileAccessGrantedToUser = !!fileAccess?.user;

                  return (
                    <List.Item key={fileAccess.id} className="!py-2.5 px-0">
                      <List.Item.Meta
                        className="justify-cente h-fullr flex flex-row items-center"
                        avatar={
                          <Avatar
                            size={42}
                            className={clsx(
                              isFileAccessMarkedToRemove(fileAccess)
                                ? 'bg-red-dark'
                                : 'bg-azure-hover',
                              fileAccess.isInherited &&
                                fileAccess.fileLimitation.accessDenied &&
                                !isFileAccessMarkedToRevokeDeny(fileAccess) &&
                                'bg-azure-hover/50',
                              'mt-1'
                            )}
                          >
                            {isFileAccessGrantedToUser ? (
                              fileAccess.user?.firstName ? (
                                fileAccess.user?.firstName?.charAt(0)
                              ) : (
                                fileAccess.user?.email?.charAt(0)
                              )
                            ) : (
                              <Icon name="UsersIcon" className="h-5 w-5" />
                            )}
                          </Avatar>
                        }
                        title={
                          <span
                            className={clsx(
                              isFileAccessMarkedToRemove(fileAccess) &&
                                'line-through',
                              fileAccess.isInherited &&
                                fileAccess.fileLimitation.accessDenied &&
                                !isFileAccessMarkedToRevokeDeny(fileAccess) &&
                                'opacity-50'
                            )}
                          >
                            {isFileAccessGrantedToUser
                              ? fileAccess.user?.firstName
                                ? `${fileAccess.user?.firstName} ${fileAccess.user?.lastName}`
                                : fileAccess.user?.email
                              : fileAccess.group?.name}
                          </span>
                        }
                        description={
                          <div className="flex h-6 flex-row items-center justify-between gap-2">
                            <Tooltip
                              title={
                                isFileAccessGrantedToUser
                                  ? fileAccess.user?.email
                                  : fileAccess.group?.name
                              }
                            >
                              <span className="truncate text-sm">
                                {isFileAccessGrantedToUser
                                  ? fileAccess.user?.email
                                  : fileAccess.group?.name}
                              </span>
                            </Tooltip>

                            {(() => {
                              // Find the effective expiry (override or original)
                              const override = form.fileAccessesToUpdate.find(
                                (fa) => fa.id === fileAccess.id
                              );
                              const expiration =
                                override?.fileLimitation.accessExpiration ??
                                fileAccess.fileLimitation.accessExpiration;

                              // If >0, render the button
                              if (expiration > 0) {
                                return (
                                  <div className="flex flex-row items-center gap-0.5 pr-2">
                                    <span className="text-xs">
                                      {t('common.expiry')}:
                                    </span>
                                    <Tooltip
                                      title={t('common.removeExpirationDate')}
                                    >
                                      <Button
                                        size="small"
                                        type="text"
                                        className="py-0 text-xs"
                                        onClick={() =>
                                          handleFileLimitationChange(
                                            fileAccess,
                                            'accessExpiration',
                                            0 // reset to zero
                                          )
                                        }
                                      >
                                        {dayjs
                                          .utc(ticksToMs(expiration))
                                          .format('YYYY-MM-DD HH:mm [UTC]')}
                                      </Button>
                                    </Tooltip>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        }
                      />

                      {fileAccess.user?.userId == authenticatedUser?.userId ? (
                        <span>{t('common.owner')}</span>
                      ) : isFileAccessMarkedToRemove(fileAccess) ? (
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          onClick={() =>
                            handleFormFieldChange(
                              'fileAccessesToRemove',
                              form.fileAccessesToRemove.filter(
                                (f) => f.id !== fileAccess.id
                              )
                            )
                          }
                        >
                          {t('common.cancel')}
                        </Button>
                      ) : (
                        <div className="flex flex-row items-center gap-0.5">
                          {!fileAccess.isInherited && (
                            <>
                              <Segmented
                                size="small"
                                value={
                                  form.fileAccessesToUpdate.find(
                                    (fa) => fa.id === fileAccess.id
                                  )?.fileLimitation.canEdit ??
                                  fileAccess.fileLimitation.canEdit
                                }
                                onChange={(val) =>
                                  handleFileLimitationChange(
                                    fileAccess,
                                    'canEdit',
                                    val
                                  )
                                }
                                options={[
                                  {
                                    value: false,
                                    label: (
                                      <Tooltip title={t('common.viewer')}>
                                        <div>
                                          <Icon
                                            name="ViewIcon"
                                            className="mt-0.5 h-5 text-yellow-dark"
                                          />
                                        </div>
                                      </Tooltip>
                                    ),
                                  },
                                  {
                                    value: true,
                                    label: (
                                      <Tooltip title={t('common.editor')}>
                                        <div>
                                          <Icon
                                            name="SquarePenIcon"
                                            className="mt-0.5 h-5 text-green-dark"
                                          />
                                        </div>
                                      </Tooltip>
                                    ),
                                  },
                                ]}
                              />
                              <Tooltip
                                placement="top"
                                title={
                                  !fileAccess?.fileLimitation?.accessExpiration
                                    ? t('common.addExpirationDate')
                                    : t('common.editExpirationDate')
                                }
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  onClick={() => {
                                    const override =
                                      form.fileAccessesToUpdate.find(
                                        (fa) => fa.id === fileAccess.id
                                      );

                                    setSelectedFileAccessForDatePicker(
                                      fileAccess
                                    );
                                    const ticksVal =
                                      override?.fileLimitation
                                        .accessExpiration ??
                                      fileAccess.fileLimitation
                                        .accessExpiration;
                                    if (ticksVal) {
                                      setFileAccessDatePickerValue(
                                        localDateFromUtcTicks(ticksVal)
                                      );
                                    } else {
                                      setFileAccessDatePickerValue(null);
                                    }
                                    setIsFileAccessDatePickerOpen(true);
                                  }}
                                >
                                  <Icon
                                    name="CalendarFoldIcon"
                                    className="h-5 w-5"
                                  />
                                </Button>
                              </Tooltip>
                            </>
                          )}
                          {fileAccess.isInherited &&
                          fileAccess.fileLimitation.accessDenied ? (
                            isFileAccessMarkedToRevokeDeny(fileAccess) ? (
                              <Button
                                variant="text"
                                color="primary"
                                size="small"
                                onClick={() =>
                                  handleFormFieldChange(
                                    'fileAccessesRevokeDenied',
                                    [
                                      form.fileAccessesRevokeDenied.filter(
                                        (f) => f.id !== fileAccess.id
                                      ),
                                    ]
                                  )
                                }
                              >
                                {t('common.cancel')}
                              </Button>
                            ) : (
                              <Button
                                aria-label={t('common.enable')}
                                onClick={() =>
                                  handleFormFieldChange(
                                    'fileAccessesRevokeDenied',
                                    [
                                      ...form.fileAccessesRevokeDenied,
                                      fileAccess,
                                    ]
                                  )
                                }
                                className="text-green-dark"
                                variant="text"
                                color="primary"
                                size="small"
                              >
                                {t('common.enable')}
                              </Button>
                            )
                          ) : (
                            <Tooltip placement="top" title={t('common.remove')}>
                              <Button
                                onClick={() =>
                                  handleFormFieldChange(
                                    'fileAccessesToRemove',
                                    [...form.fileAccessesToRemove, fileAccess]
                                  )
                                }
                                danger
                                type="text"
                                size="small"
                              >
                                <Icon name="TrashIcon" className="h-5 w-5" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </List.Item>
                  );
                }}
              />
              {isLoading && (
                <>
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                </>
              )}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      )}
      <div className="mt-4 flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          {selectedFiles.length === 1 && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                copyToClipboard(
                  window.origin + '/file/' + selectedFiles?.[0]?.uid
                );
                messageApi.success(t('common.linkCopied'));
              }}
            >
              {t('common.copyLink')}
            </Button>
          )}
        </div>
        <div className="flex flex-row items-center justify-center gap-2">
          <Button size="middle" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            size="middle"
            disabled={isSubmitButtonDisabled}
            type="primary"
            onClick={handleSubmit}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
      {/* → Picker “globale” per expiry */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        value={limitationsDatePickerValue}
        open={isLimitationsDatePickerOpen}
        onOpenChange={setIsLimitationsDatePickerOpen}
        onChange={(date) => {
          if (!date) return;
          // fine del giorno UTC selezionato
          const msEndUtc = dayjs
            .utc(date.format('YYYY-MM-DD'))
            .endOf('day')
            .valueOf();
          handleFormFieldChange('expiry', msToTicks(msEndUtc));
          setIsLimitationsDatePickerOpen(false);
          setLimitationsDatePickerValue(null);
        }}
        className="pointer-events-none absolute opacity-0"
      />

      {/* → Picker per singolo fileAccess */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        value={fileAccessDatePickerValue}
        open={isFileAccessDatePickerOpen}
        onOpenChange={setIsFileAccessDatePickerOpen}
        onChange={(date) => {
          if (!date || !selectedFileAccessForDatePicker) return;
          const msEndUtc = dayjs
            .utc(date.format('YYYY-MM-DD'))
            .endOf('day')
            .valueOf();
          handleFileLimitationChange(
            selectedFileAccessForDatePicker,
            'accessExpiration',
            msToTicks(msEndUtc)
          );
          setIsFileAccessDatePickerOpen(false);
          setFileAccessDatePickerValue(null);
        }}
        className="pointer-events-none absolute opacity-0"
      />
    </Form>
  );
};

export default FileShareForm;
